<script setup lang="ts">
import { Ref, ref, computed } from "vue";
import { useApiClient } from "@/api/client";

import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
const { t } = useI18n();

import { useBaseStore } from "@/stores/base";
import dayjs from "dayjs";
import UserListViewDTO = App.Data.UserListViewDTO;
import { debounce } from "vue-debounce";
import useDialog from "@/ui/composables/useDialog";

const headers = computed(() => {
	let h = [
		{
			title: t("users.overview.headerName"),
			align: "start",
			key: "name",
		},
		{
			title: t("users.overview.headerCompany"),
			align: "start",
			key: "company",
		},
		{
			title: t("users.overview.headerRoles"),
			align: "start",
			key: "roles",
			sortable: false,
		},
	];

	if (useBaseStore().currentUser?.is_motives_admin) {
		h.push({
			title: t("users.overview.headerLastLoginAt"),
			align: "start",
			key: "last_login_at",
		});
	}

	h.push({
		title: t("users.overview.headerActions"),
		key: "actions",
		align: "center",
		width: 140,
		sortable: false,
	});

	return h;
});

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
	search?: string;
}

const loading: Ref<boolean | string> = ref(false);
const total: Ref<number> = ref(0);
const items: Ref<UserListViewDTO[] | []> = ref([]);
const itemsPerPage: Ref<number> = ref(10);

const loadItems = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage: per_page, sortBy, search } = options;

	const sort = sortBy[0]
		? (sortBy[0]["order"] === "desc" ? "-" : "") + sortBy[0]["key"]
		: undefined;

	const response = await useApiClient("users").getUserListViews({
		page,
		per_page,
		sort,
		"filter[search]": search,
	});
	if (response.isOk()) {
		items.value = response.value.data;
		total.value = response.value.total;

		loading.value = false;
	}
}, 300);

const removeUser = async (uuid: string) => {
	const confirm = await useDialog().confirm({
		title: "Delete User",
		message: `Are you sure you want to delete this user?`,
		isDelete: true,
	});

	if (!confirm) return;

	loading.value = "secondary";
	const response = await toastApiResult(useApiClient("users").removeUser(uuid));
	if (response.isOk()) {
		await loadItems(tableOptions.value);
		loading.value = false;
	}
};

const impersonateUser = async (uuid: string) => {
	const response = await toastApiResult(
		useApiClient("users").impersonateUser(uuid)
	);
	if (response.isOk()) {
		window.location.href = "/home";
	}
};

const props = defineProps<{
	search: string;
}>();

const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 10,
	sortBy: [],
	search: "",
});
</script>

<template>
	<v-data-table-server
		:headers="headers"
		:items="items"
		:items-per-page="itemsPerPage"
		:items-length="total"
		@update:options="loadItems"
		class="elevation-0"
		:multi-sort="false"
		:loading="loading"
		:search="props.search"
	>
		<template v-slot:item.name="{ item }">
			<div class="d-flex align-center my-1 font-weight-bold">
				{{ item.name }}
			</div>
		</template>
		<template v-slot:item.roles="{ item }">
			<!--				{{ item.raw?.role?.name ?? "-" }}-->
			<v-chip
				v-for="role in item?.roles"
				:key="role.id"
				variant="flat"
				class="mr-2"
			>
				{{ role.name }}
			</v-chip>
		</template>
		<template v-slot:item.last_login_at="{ item }">
			{{
				item.last_login_at
					? dayjs(item.last_login_at).format("DD MMM YYYY HH:mm")
					: "-"
			}}
		</template>
		<template v-slot:item.actions="{ item }">
			<div v-if="item.is_locked_for_editing">
				<v-btn color="primary" variant="text" disabled>
					<i class="fa-duotone fa-lock-keyhole fa-lg text-primary"> </i>
				</v-btn>
				<v-tooltip activator="parent" location="top" content-class="text-small">
					{{ t("users.overview.userLocked") }}
				</v-tooltip>
			</div>
			<template v-else>
				<v-btn
					size="x-small"
					icon="fa: fa-light fa-pencil"
					color="primary"
					variant="text"
					:to="`/users/edit/${item.uuid}`"
				/>
				<v-btn
					size="x-small"
					@click="removeUser(item.uuid)"
					icon="fa: fa-light fa-trash-can"
					color="error"
					variant="text"
				/>
				<v-tooltip text="Impersonate User">
					<template v-slot:activator="{ props }">
						<v-btn
							size="small"
							v-if="
								useBaseStore().HAS_PERMISSION('can_impersonate_users') &&
								!useBaseStore().currentUser?.originalUser
							"
							:disabled="item.uuid === useBaseStore().currentUser?.uuid"
							@click="impersonateUser(item.uuid)"
							icon="fa: fa-duotone fa-person-half-dress"
							color="info"
							variant="text"
							v-bind="props"
						/>
					</template>
				</v-tooltip>
			</template>
		</template>
	</v-data-table-server>
</template>

<style scoped></style>
